import { useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import { availableForecastsAtom } from '../atoms';
import {
  EditVolumeSourceModalView,
  type EditVolumeSourceModalViewProps,
} from 'pages/packages/show/edit-volume-source-modal/edit-volume-source-modal-view';

type EditVolumeSourceModalProps = Omit<
  EditVolumeSourceModalViewProps,
  'data' | 'isLoading' | 'error'
> & {
  /** The evaluation's package; selects its Type Curve Scenario's curves. */
  packageId: number;
  /** The row's own Type Curve Area; narrows the list to it. */
  typeCurveAreaName?: string;
};

/**
 * Edit Volume Source modal — Jotai data wrapper. Owns the lazy
 * `availableForecastsAtom` fetch (triggered once per open) and feeds the
 * presentational `EditVolumeSourceModalView`. On top of the View's props it takes
 * the evaluation's package and the row's area, which select the Type Curve
 * Scenario's curves for the slot; the eval3-new feature renders the View
 * directly with React-Query-supplied data instead.
 *
 * @param {EditVolumeSourceModalProps} props - The modal state, callbacks, and slot context.
 * @return {JSX.Element} The Edit Volume Source modal.
 */
export const EditVolumeSourceModal = ({
  packageId,
  typeCurveAreaName,
  ...props
}: EditVolumeSourceModalProps) => {
  const { isOpen, slotId, currentVolumeSourceId } = props;
  const hasTriggeredRef = useRef(false);

  // Fetch available forecasts using lazy atom (triggers on demand)
  const [forecastsLoadable, triggerFetch] = useAtom(
    availableForecastsAtom({
      slotId: slotId || '',
      packageId,
      currentVolumeSourceId,
      typeCurveAreaName,
    })
  );

  // Extract primitive values to use as stable deps (objects cause infinite loops)
  const state = forecastsLoadable.state;
  const isDataNull =
    state === 'hasData' ? forecastsLoadable.data === null : false;

  // Reset trigger flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasTriggeredRef.current = false;
    }
  }, [isOpen]);

  // Trigger fetch ONCE per modal open (idle or error)
  useEffect(() => {
    if (isOpen && slotId && !hasTriggeredRef.current) {
      const isIdle = state === 'hasData' && isDataNull;
      const isError = state === 'hasError';

      if (isIdle || isError) {
        hasTriggeredRef.current = true;
        triggerFetch();
      }
    }
  }, [isOpen, slotId, state, isDataNull, triggerFetch]);

  const isLoading = forecastsLoadable.state === 'loading';
  const error =
    forecastsLoadable.state === 'hasError'
      ? forecastsLoadable.error instanceof Error
        ? forecastsLoadable.error.message
        : 'Failed to load available forecasts'
      : null;
  const data =
    forecastsLoadable.state === 'hasData' ? forecastsLoadable.data : null;

  return (
    <EditVolumeSourceModalView
      {...props}
      data={data}
      isLoading={isLoading}
      error={error}
    />
  );
};
